import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { likedFolderService } from 'shared/src/api';
import type { GetFolderDetailResponse } from 'shared/src/api/likedFolderService/getFolderDetail/types';
import { queryKey } from '../../../constant/queryKey';
import { DELIVERY_REQUEST_LIST, type DeliveryToggleType } from '../constants';
import { convertFolderGoodsItemToGoodsData } from '../utils/convertFolderGoodsItemToGoodsData';

interface UseFolderDetailParams {
  folderSno: number;
  order?: string;
  categorySno?: number;
  deliveryToggleType?: DeliveryToggleType;
}

const resolveDeliveryToggleType = (
  toggleList: string[] | null,
): DeliveryToggleType => {
  if (toggleList == null || toggleList.length === 0) return 'standard';
  const hasShak = toggleList.includes('shak');
  const hasToday = toggleList.includes('today');
  if (hasShak && hasToday) return 'fast';
  if (hasShak) return 'shak';
  if (hasToday) return 'today';
  return 'standard';
};

const selectFolderDetail = (data: InfiniteData<GetFolderDetailResponse>) => ({
  pages: data.pages,
  goods: data.pages.flatMap(page =>
    page.goods.map(convertFolderGoodsItemToGoodsData),
  ),
  title: data.pages[0]?.title ?? null,
  listId: data.pages[0]?.list_id ?? null,
  listParams: data.pages[0]?.list_params ?? null,
  totalCount: data.pages[data.pages.length - 1]?.count ?? 0,
  deliveryToggleType: resolveDeliveryToggleType(
    data.pages[0]?.delivery_type_toggle_list ?? null,
  ),
});

export const useFolderDetail = ({
  folderSno,
  order,
  categorySno,
  deliveryToggleType,
}: UseFolderDetailParams) => {
  const deliveryTypeList = deliveryToggleType
    ? [...DELIVERY_REQUEST_LIST[deliveryToggleType]]
    : undefined;
  return useInfiniteQuery({
    queryKey: queryKey.folderDetail({
      folderSno,
      order,
      categorySno,
      deliveryTypeList,
    }),
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      likedFolderService.getFolderDetail({
        folder_sno: folderSno,
        last_sno: pageParam,
        order,
        category_sno: categorySno,
        delivery_type_list: deliveryTypeList,
      }),
    initialPageParam: undefined,
    getNextPageParam: page => {
      const last = page.goods[page.goods.length - 1];
      if (last == null) return undefined;
      return last.sno;
    },
    select: selectFolderDetail,
    placeholderData: keepPreviousData,
  });
};
